import React from 'react';

/** @typedef {'top'|'right'|'bottom'|'left'} BorderSide */

/**
 * Removes duplicate shared borders while preserving the outer frame.
 * Top and right own shared grid lines; bottom and left complete the last visible row and first column.
 *
 * @param {Object} options
 * @param {boolean} [options.borderTop]
 * @param {boolean} [options.borderRight]
 * @param {boolean} [options.borderBottom]
 * @param {boolean} [options.borderLeft]
 * @param {boolean} [options.isFirstColumn]
 * @param {boolean} [options.isLastRow]
 * @param {Partial<import('constants/calendarSettings').Margin>} [options.margin]
 * @returns {{top: boolean|undefined, right: boolean|undefined, bottom: boolean|undefined, left: boolean|undefined}}
 */
export const getVisibleDayBorders = ({
  borderTop,
  borderRight,
  borderBottom,
  borderLeft,
  isFirstColumn,
  isLastRow,
  margin = {},
}) => ({
  top: borderTop,
  right: borderRight,
  bottom: borderBottom && (!borderTop || isLastRow || (margin.top || 0) + (margin.bottom || 0) > 0),
  left: borderLeft && (!borderRight || isFirstColumn || (margin.left || 0) + (margin.right || 0) > 0),
});

/**
 * Draws each border inside the cell's outer margin; content padding does not move it.
 *
 * @param {Object} props
 * @param {{width: number, height: number, margin: import('constants/calendarSettings').Margin}} props.cellShape
 * @param {string} [props.color]
 * @param {Partial<Record<BorderSide, string>>} [props.colors]
 * @param {boolean} [props.borderTop]
 * @param {boolean} [props.borderRight]
 * @param {boolean} [props.borderBottom]
 * @param {boolean} [props.borderLeft]
 * @param {boolean} [props.isFirstColumn]
 * @param {boolean} [props.isLastRow]
 * @returns {React.ReactElement|null}
 */
const DayBorders = ({
  cellShape,
  color,
  colors = {},
  borderTop,
  borderRight,
  borderBottom,
  borderLeft,
  isFirstColumn,
  isLastRow,
}) => {
  const { width, height, margin } = cellShape;
  const { left, right: rightMargin } = margin;
  const right = width - rightMargin;
  const { top } = margin;
  const bottom = height - margin.bottom;
  const visibleBorders = getVisibleDayBorders({
    borderTop,
    borderRight,
    borderBottom,
    borderLeft,
    isFirstColumn,
    isLastRow,
    margin,
  });
  if (right <= left || bottom <= top) return null;
  const borders = [
    { side: 'top', enabled: visibleBorders.top, x1: left, x2: right, y1: top, y2: top },
    { side: 'right', enabled: visibleBorders.right, x1: right, x2: right, y1: top, y2: bottom },
    { side: 'bottom', enabled: visibleBorders.bottom, x1: left, x2: right, y1: bottom, y2: bottom },
    { side: 'left', enabled: visibleBorders.left, x1: left, x2: left, y1: top, y2: bottom },
  ];

  return (
    <>
      {borders.map(({ side, enabled, ...coordinates }) =>
        enabled ? <line key={side} {...coordinates} stroke={colors[side] ?? color} strokeWidth="1" /> : null,
      )}
    </>
  );
};

export default DayBorders;
